/**
 * Splits a supplier's twelve months of obligations into the two things the
 * page is actually about: what still has to be sent, and what is already in.
 */
import type { SupplierUploadTracking } from '../../../lib/api/barratt';
import { daysOverdue, isRequested } from '../uploadCycle';
import type { ObligationPeriod } from './useMyObligations';

export interface UploadSummary {
  /** Months with at least one file still to send, newest first. */
  openPeriods: ObligationPeriod[];
  /** Months where every file is in, newest first. */
  completePeriods: ObligationPeriod[];
  /** Files the customer asked for and has not had. */
  outstandingCount: number;
  /** Of those, the ones already past their due date. */
  overdueCount: number;
  /** The requested file that has been waiting longest, or null. */
  oldestOutstanding: SupplierUploadTracking | null;
  /** Months fully in, out of months on record. */
  completeCount: number;
  periodCount: number;
}

/**
 * A file the supplier actually owes.
 *
 * `useMyObligations` mints a row for every month the customer has not asked
 * for, so a supplier can still send an earlier file. Counting those as work
 * would put a card and an "outstanding" badge on a month nobody is waiting for
 * — with the banner, which has always excluded them, calling the same supplier
 * up to date on the line above.
 */
export function needsAction(row: SupplierUploadTracking): boolean {
  return row.status !== 'uploaded' && isRequested(row);
}

/**
 * @param periods - Grouped obligations from `useMyObligations`, newest first
 * @returns The open / complete split plus the counts the summary banner shows
 */
export function summariseObligations(periods: ObligationPeriod[]): UploadSummary {
  const openPeriods: ObligationPeriod[] = [];
  const completePeriods: ObligationPeriod[] = [];
  const outstanding: SupplierUploadTracking[] = [];

  for (const period of periods) {
    if (period.rows.some(needsAction)) {
      openPeriods.push(period);
    } else {
      completePeriods.push(period);
    }
    outstanding.push(...period.rows.filter(needsAction));
  }

  // Longest-waiting first: the supplier's next move is the top of this list.
  // A row with no due date sorts last rather than throwing.
  const byDueDate = [...outstanding].sort((a, b) => (a.due_date ?? '9999').localeCompare(b.due_date ?? '9999'));

  return {
    openPeriods,
    completePeriods,
    outstandingCount: outstanding.length,
    overdueCount: outstanding.filter((row) => daysOverdue(row.due_date) > 0).length,
    oldestOutstanding: byDueDate[0] ?? null,
    completeCount: completePeriods.length,
    periodCount: periods.length,
  };
}
